import type { ReactNode } from 'react'

/**
 * A small, safe Markdown renderer for agent answers and rationales: headings, paragraphs,
 * nested lists, GFM tables (with alignment, and numeric columns right-aligned), fenced code,
 * block quotes, rules, and inline bold, italic, code and links. No raw HTML is rendered.
 */
export function Markdown({ text, className = '' }: { text: string; className?: string }) {
  return <div className={`md ${className}`}>{blocks(parseBlocks(text.replace(/\r\n?/g, '\n')))}</div>
}

export type Block =
  | { t: 'h'; level: number; text: string }
  | { t: 'p'; text: string }
  | { t: 'hr' }
  | { t: 'code'; lang: string; text: string }
  | { t: 'quote'; children: Block[] }
  | { t: 'list'; ordered: boolean; start: number; items: ListItem[] }
  | { t: 'table'; align: ('left' | 'right' | 'center' | null)[]; head: string[]; rows: string[][] }

export type ListItem = { text: string; checked: boolean | null; children: Block[] }

const LIST = /^(\s*)([-*+]|\d{1,3}[.)])\s+(.*)$/
const FENCE = /^\s*(```|~~~)\s*([\w+-]*)\s*$/
const HR = /^\s*([-*_])(\s*\1){2,}\s*$/
const TABLE_SEP = /^\s*\|?\s*:?-{1,}:?\s*(\|\s*:?-{1,}:?\s*)*\|?\s*$/

export function parseBlocks(src: string): Block[] {
  const lines = src.split('\n')
  const out: Block[] = []
  let i = 0
  const isStart = (l: string, next?: string) =>
    FENCE.test(l) ||
    /^\s*#{1,6}\s/.test(l) ||
    HR.test(l) ||
    /^\s*>/.test(l) ||
    LIST.test(l) ||
    (l.includes('|') && next !== undefined && TABLE_SEP.test(next) && next.includes('-'))
  while (i < lines.length) {
    const line = lines[i] as string
    if (!line.trim()) {
      i++
      continue
    }
    const fence = FENCE.exec(line)
    if (fence) {
      const body: string[] = []
      i++
      while (i < lines.length && !new RegExp(`^\\s*${fence[1]}\\s*$`).test(lines[i] as string)) body.push(lines[i++] as string)
      i++
      out.push({ t: 'code', lang: fence[2] ?? '', text: body.join('\n') })
      continue
    }
    const h = /^\s*(#{1,6})\s+(.*?)\s*#*\s*$/.exec(line)
    if (h) {
      out.push({ t: 'h', level: (h[1] as string).length, text: h[2] as string })
      i++
      continue
    }
    if (HR.test(line)) {
      out.push({ t: 'hr' })
      i++
      continue
    }
    if (line.includes('|') && i + 1 < lines.length && TABLE_SEP.test(lines[i + 1] as string) && (lines[i + 1] as string).includes('-')) {
      const head = cells(line)
      const align = cells(lines[i + 1] as string).map(c => {
        const l = c.startsWith(':')
        const r = c.endsWith(':')
        return l && r ? 'center' : r ? 'right' : l ? 'left' : null
      })
      i += 2
      const rows: string[][] = []
      while (i < lines.length && (lines[i] as string).includes('|') && (lines[i] as string).trim()) rows.push(cells(lines[i++] as string))
      out.push({ t: 'table', align: head.map((_, k) => align[k] ?? null), head, rows })
      continue
    }
    if (/^\s*>/.test(line)) {
      const body: string[] = []
      while (i < lines.length && /^\s*>/.test(lines[i] as string)) body.push((lines[i++] as string).replace(/^\s*>\s?/, ''))
      out.push({ t: 'quote', children: parseBlocks(body.join('\n')) })
      continue
    }
    if (LIST.test(line)) {
      const block: string[] = []
      const head = LIST.exec(line) as RegExpExecArray
      const indent = (head[1] as string).length
      const ordered = /\d/.test(head[2] as string)
      while (i < lines.length) {
        const l = lines[i] as string
        // switching between numbers and bullets at the same level starts a new list
        const m = LIST.exec(l)
        if (block.length && m && (m[1] as string).length <= indent + 1 && /\d/.test(m[2] as string) !== ordered) break
        if (!l.trim()) {
          // a blank line ends the list unless an indented or list line follows
          const next = lines[i + 1]
          if (next !== undefined && (LIST.test(next) || /^\s{2,}\S/.test(next))) {
            block.push('')
            i++
            continue
          }
          break
        }
        if (block.length && !LIST.test(l) && !/^\s+\S/.test(l) && isStart(l, lines[i + 1])) break
        block.push(l)
        i++
      }
      out.push(parseList(block))
      continue
    }
    const para: string[] = []
    while (i < lines.length && (lines[i] as string).trim() && !(para.length && isStart(lines[i] as string, lines[i + 1])))
      para.push((lines[i++] as string).trim())
    out.push({ t: 'p', text: para.join('\n') })
  }
  return out
}

function parseList(lines: string[]): Block {
  const first = LIST.exec(lines[0] as string) as RegExpExecArray
  const base = (first[1] as string).length
  const ordered = /\d/.test(first[2] as string)
  const items: { head: string; rest: string[] }[] = []
  for (const l of lines) {
    const m = LIST.exec(l)
    if (m && (m[1] as string).length <= base + 1) items.push({ head: m[3] as string, rest: [] })
    else items.at(-1)?.rest.push(l.slice(Math.min(base + 2, l.length - l.trimStart().length)))
  }
  return {
    t: 'list',
    ordered,
    start: ordered ? Number.parseInt(first[2] as string, 10) || 1 : 1,
    items: items.map(it => {
      const task = /^\[([ xX])\]\s+(.*)$/.exec(it.head)
      const nested = it.rest.join('\n')
      return {
        text: task ? (task[2] as string) : it.head,
        checked: task ? task[1] !== ' ' : null,
        children: nested.trim() ? parseBlocks(nested) : [],
      }
    }),
  }
}

function cells(line: string): string[] {
  let s = line.trim()
  if (s.startsWith('|')) s = s.slice(1)
  if (s.endsWith('|') && !s.endsWith('\\|')) s = s.slice(0, -1)
  const out: string[] = []
  let cur = ''
  for (let k = 0; k < s.length; k++) {
    const ch = s[k]
    if (ch === '\\' && s[k + 1] === '|') {
      cur += '|'
      k++
    } else if (ch === '|') {
      out.push(cur.trim())
      cur = ''
    } else cur += ch
  }
  out.push(cur.trim())
  return out
}

const NUMERIC = /^[(−–+-]?\s*[$€£¥]?\s*[−–-]?[\d.,]+\s*[%KMBkmbx×]?\)?$|^n\/a$|^—$|^-$/

function blocks(bs: Block[], key = 'b'): ReactNode[] {
  return bs.map((b, n) => {
    const k = `${key}${n}`
    switch (b.t) {
      case 'h': {
        const level = Math.min(6, b.level + 2)
        const Tag = `h${level}` as 'h3'
        return (
          <Tag key={k} className={`md-h md-h${b.level}`}>
            {inline(b.text, k)}
          </Tag>
        )
      }
      case 'p':
        return (
          <p key={k}>
            {b.text.split('\n').map((l, j, arr) => (
              <span key={j}>
                {inline(l, `${k}.${j}`)}
                {j < arr.length - 1 && <br />}
              </span>
            ))}
          </p>
        )
      case 'hr':
        return <hr key={k} />
      case 'code':
        return (
          <pre key={k} className="md-code" data-lang={b.lang || undefined}>
            <code>{b.text}</code>
          </pre>
        )
      case 'quote':
        return <blockquote key={k}>{blocks(b.children, `${k}q`)}</blockquote>
      case 'list': {
        const Tag = b.ordered ? 'ol' : 'ul'
        return (
          <Tag
            key={k}
            start={b.ordered && b.start !== 1 ? b.start : undefined}
            className={b.items.some(it => it.checked !== null) ? 'tasks' : undefined}
          >
            {b.items.map((it, j) => (
              <li key={j}>
                {it.checked !== null && <input type="checkbox" checked={it.checked} readOnly tabIndex={-1} aria-hidden />}
                {inline(it.text, `${k}.${j}`)}
                {it.children.length > 0 && blocks(it.children, `${k}.${j}c`)}
              </li>
            ))}
          </Tag>
        )
      }
      case 'table': {
        // columns without explicit alignment are right-aligned when they hold numbers
        const align = b.align.map((a, c) => {
          if (a) return a
          const vals = b.rows.map(r => strip(r[c] ?? '')).filter(Boolean)
          return vals.length && vals.filter(v => NUMERIC.test(v)).length >= Math.ceil(vals.length * 0.6) ? 'right' : 'left'
        })
        const strong = (r: string[]) => r.filter(Boolean).length > 0 && r.filter(Boolean).every(c => /^\*\*.*\*\*$/.test(c))
        return (
          <div key={k} className="md-table">
            <table>
              <thead>
                <tr>
                  {b.head.map((h, c) => (
                    <th key={c} style={{ textAlign: align[c] ?? 'left' }}>
                      {inline(h, `${k}h${c}`)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {b.rows.map((r, j) => (
                  <tr key={j} className={strong(r) ? 'strong' : undefined}>
                    {b.head.map((_, c) => (
                      <td key={c} style={{ textAlign: align[c] ?? 'left' }} className={align[c] === 'right' ? 'num' : undefined}>
                        {inline(r[c] ?? '', `${k}r${j}c${c}`)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }
      default:
        return null
    }
  })
}

const strip = (s: string) => s.replace(/\*\*|__|`/g, '').trim()

const INLINE =
  /(`+)([\s\S]*?[^`])\1(?!`)|\*\*([^*]+(?:\*(?!\*)[^*]*)*)\*\*|__([^_]+)__|\*([^*\s][^*]*?)\*|(?<![\w])_([^_\s][^_]*?)_(?![\w])|\[([^\]]+)\]\(([^)\s]+)(?:\s+"[^"]*")?\)|(https?:\/\/[^\s<>()]+[^\s<>().,;:!?'"])|\\([\\`*_{}[\]()#+\-.!|>~])/g

/** Links open only http(s), mailto or in-app paths; anything else stays text. */
export function safeHref(href: string): string | null {
  if (/^(https?:|mailto:)/i.test(href)) return href
  if (href.startsWith('/') && !href.startsWith('//')) return href
  return null
}

export function inline(s: string, key: string): ReactNode[] {
  const out: ReactNode[] = []
  let last = 0
  let n = 0
  INLINE.lastIndex = 0
  const re = new RegExp(INLINE.source, 'g')
  for (let m = re.exec(s); m; m = re.exec(s)) {
    if (m.index > last) out.push(s.slice(last, m.index))
    const k = `${key}-${n++}`
    if (m[2] !== undefined) out.push(<code key={k}>{m[2]}</code>)
    else if (m[3] !== undefined || m[4] !== undefined) out.push(<strong key={k}>{inline((m[3] ?? m[4]) as string, k)}</strong>)
    else if (m[5] !== undefined || m[6] !== undefined) out.push(<em key={k}>{inline((m[5] ?? m[6]) as string, k)}</em>)
    else if (m[7] !== undefined) {
      const href = safeHref(m[8] as string)
      out.push(
        href ? (
          <a key={k} href={href} target={href.startsWith('/') ? undefined : '_blank'} rel="noreferrer noopener">
            {inline(m[7], k)}
          </a>
        ) : (
          <span key={k}>{m[7]}</span>
        ),
      )
    } else if (m[9] !== undefined)
      out.push(
        <a key={k} href={m[9]} target="_blank" rel="noreferrer noopener">
          {m[9]}
        </a>,
      )
    else if (m[10] !== undefined) out.push(m[10])
    last = m.index + m[0].length
  }
  if (last < s.length) out.push(s.slice(last))
  return out
}
